import { ViewBooking } from "collins-js-sdk";
import { uploadAttachment } from "../../../../../../APIs/Messages/UploadAttachment";
import { Attachments, ToElement } from "../../../../../../types/messages";
import { CollinsUser } from "../../../../../../types/user";
import {
  EmailAttachment,
  VenueGroupConfig
} from "../../../../../../types/VenueGroup";
import { EmailMessages, VenueConfig } from "../../../../../../types/venues";
import {
  getCustomerName,
  getGuestCount
} from "../../../../../../utils/booking";
import {
  getBookingEndTimeAsString,
  getBookingTimeAsString
} from "../../../../../../utils/singleBooking";

export type newEmailType = {
  fromName: string;
  fromEmail: string;
  to: ToElement[];
  cc: string;
  type: string;
  attachments: Attachments[];
  preDefinedSelectedAttachments?: EmailAttachment[];
  message: string;
  includeSummary: boolean;
  includeCustomerRequests: boolean;
  noSalutation: boolean;
};

type GetNewEmailType = {
  selectedBooking?: ViewBooking;
  venueGroupConfig?: VenueGroupConfig | null;
  venueConfig?: VenueConfig | null;
  user?: CollinsUser;
};
export const getNewEmail = ({
  selectedBooking,
  venueGroupConfig,
  venueConfig,
  user
}: GetNewEmailType): newEmailType | undefined => {
  if (!selectedBooking || !venueGroupConfig || !venueConfig || !user) {
    return undefined;
  }

  const { fromName, fromEmail } = getEmailFrom({
    from: "site",
    user,
    venueConfig,
    venueGroupConfig
  });
  return {
    fromName,
    fromEmail,
    to: [
      {
        name: getCustomerName(selectedBooking),
        address: selectedBooking.email ?? ""
      }
    ],
    cc: "",
    type: "general",
    attachments: [],
    preDefinedSelectedAttachments: [],
    message: "",
    includeSummary: true,
    includeCustomerRequests: true,
    noSalutation: !!venueConfig.no_email_salutation
  };
};

type GetEmailFromType = {
  from: "site" | "me";
  venueGroupConfig: VenueGroupConfig;
  venueConfig: VenueConfig;
  user: CollinsUser;
};
export const getEmailFrom = ({
  from,
  venueGroupConfig,
  venueConfig,
  user
}: GetEmailFromType) => {
  let fromName = "";
  let fromEmail = "";

  if (venueConfig.booking_emails_from_name) {
    fromName = venueConfig.booking_emails_from_name;
  } else {
    fromName = venueGroupConfig.booking_emails_from_name;
  }

  if (venueConfig.booking_emails_from_address) {
    fromEmail = venueConfig.booking_emails_from_address;
  } else {
    fromEmail = venueGroupConfig.booking_emails_from_address;
  }

  if (from === "me") {
    fromName = [user.first_name, user.last_name].join(" ").trim();
    // "Send from me" should just change the send from display name, not the actual sender email address
    // fromEmail = user.email ?? "";
  }
  return { fromName, fromEmail };
};

type AddAttachmentResponse = {
  error: boolean;
  errorMessage: string;
  attachments: Attachments[];
};
type AddAttachmentProps = {
  bookingId: string;
  files: File[];
};
export const addAttachment = async ({
  bookingId,
  files
}: AddAttachmentProps): Promise<AddAttachmentResponse> => {
  const attachments: Attachments[] = [];
  let error = false;
  let errorMessage = "";

  try {
    const responses = await Promise.all(
      files.map((file) => uploadAttachment(bookingId, file))
    );

    responses.forEach((response) => {
      const fileResponse = response?.files?.[0];
      if (fileResponse?.path && fileResponse.success) {
        attachments.push({
          filename: fileResponse.filename,
          path: fileResponse.path
        });
      } else {
        error = true;
        errorMessage = "the attachment has failed to upload. Please try again.";
      }
    });
  } catch {
    error = true;
    errorMessage =
      "The attachment has failed to upload. Please check your internet connectivity.";
  }
  return { attachments, error, errorMessage };
};

type GetPredefinedEmailMessagesProps = {
  venueConfig: VenueConfig | null;
  venueGroupConfig: VenueGroupConfig | null;
};
export const getPredefinedEmailMessages = ({
  venueConfig,
  venueGroupConfig
}: GetPredefinedEmailMessagesProps): EmailMessages[] | undefined => {
  if (!venueConfig || !venueGroupConfig) {
    return [];
  }
  // get predefined email messages for dropdown
  let emailMessages = venueGroupConfig.email_messages;
  if (venueConfig.override_email_messages) {
    emailMessages = venueConfig.email_messages;
  }

  return emailMessages?.filter((m) => m.contents !== "" && m.name !== "") ?? [];
};

type GetPredefinedEmailAttachmentsProps = {
  venueConfig: VenueConfig | null;
  venueGroupConfig: VenueGroupConfig | null;
};
export const getPredefinedEmailAttachments = ({
  venueConfig,
  venueGroupConfig
}: GetPredefinedEmailAttachmentsProps): EmailAttachment[] => {
  if (!venueConfig || !venueGroupConfig) {
    return [];
  }
  // get predefined email attachments for dropdown
  let emailAttachments = venueGroupConfig.email_attachments;
  if (venueConfig.override_email_attachments) {
    emailAttachments = venueConfig.email_attachments ?? [];
  }

  return emailAttachments?.filter((m) => m.name !== "" && m._id !== "") ?? [];
};

type GetBookingDetailsProps = {
  selectedBooking?: ViewBooking;
};
export const getBookingDetails = ({
  selectedBooking
}: GetBookingDetailsProps) => {
  if (!selectedBooking) {
    return "";
  }
  return `${getGuestCount([selectedBooking])} guests on ${new Date(
    selectedBooking.date ?? ""
  )?.toLocaleDateString(window.locale, {
    dateStyle: "short"
  })} from ${getBookingTimeAsString(
    selectedBooking
  )} to ${getBookingEndTimeAsString(selectedBooking)}`;
};

type GetIsSendDisabledType = {
  venueGroupConfig: VenueGroupConfig | null;
  venueConfig: VenueConfig | null;
  selectedBooking?: ViewBooking;
};
export const getIsSendDisabled = ({
  venueGroupConfig,
  venueConfig,
  selectedBooking
}: GetIsSendDisabledType): boolean => {
  const hasSelectedBookingEmail = !!selectedBooking?.email;
  const hasVenueConfigEmailAddress = !!venueConfig?.booking_emails_from_address;
  const hasVenueGroupBookingEmailFrom =
    !!venueGroupConfig?.booking_emails_from_address;

  return (
    !hasSelectedBookingEmail ||
    !(hasVenueGroupBookingEmailFrom || hasVenueConfigEmailAddress)
  );
};

export const getSendDisabledReasons = ({
  venueGroupConfig,
  venueConfig,
  selectedBooking
}: GetIsSendDisabledType): string[] => {
  const reasons: string[] = [];

  if (!selectedBooking?.email) {
    reasons.push("Customer email address is missing from the booking");
  }

  const hasVenueConfigEmailAddress = !!venueConfig?.booking_emails_from_address;
  const hasVenueGroupBookingEmailFrom =
    !!venueGroupConfig?.booking_emails_from_address;

  if (!hasVenueGroupBookingEmailFrom && !hasVenueConfigEmailAddress) {
    reasons.push(
      "Venue 'from' email address is not configured (check venue or venue group settings)"
    );
  }

  return reasons;
};
